import { GTFonts } from '../../theme';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Pressable, StyleSheet, View, Platform } from 'react-native';
import { ModalContext } from '../../Context/modalContext';
import Typography from '../Reusables/Typography';
import { useTheme } from '../../theme';
import Button from '../Reusables/Button';
import MaterialCommunityIcons from '@react-native-vector-icons/material-design-icons/static';
import { useDispatch, useSelector } from 'react-redux';
import {
  ProductSortOption,
  selectProductSort,
  setProductSort,
} from '../../redux/selectors';
import CustomBottomSheet from '../Reusables/bottomSheet/CustomBottomSheet';
import { BottomSheetModal } from '@gorhom/bottom-sheet';
import { s, fs, iconSize, v, borderWidth } from '../../utils/libs';
import { isTablet } from 'react-native-device-info';
import { isEmployeeAdminRole } from '../../utils/roles';
import { RootState } from '../../redux/types';

// A–Z is the default sort. The legacy 'default' key is normalized to
// 'name_asc' on rehydrate and is intentionally omitted from this list.
type SortOptionEntry = { key: ProductSortOption; label: string; icon: string; adminOnly?: boolean };
const allOptions: SortOptionEntry[] = [
  {
    key: 'name_asc',
    label: 'A-Z (Default)',
    icon: 'sort-alphabetical-ascending',
  },
  { key: 'in_stock', label: 'In Stock', icon: 'package-variant', adminOnly: true },
  { key: 'recent', label: 'Recently added', icon: 'clock-outline' },
  { key: 'price_desc', label: 'Most Expensive', icon: 'currency-usd' },
];

const FilterModal = () => {
  const { filterModal, setFilterModal } = useContext(ModalContext);
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);
  const dispatch = useDispatch();
  const employeeRole = useSelector(
    (state: RootState) => state.auth.inApp.account.employeeInfo?.bp_employee_role,
  );
  const isAdmin = isEmployeeAdminRole(employeeRole);
  const options = useMemo(
    () => allOptions.filter(o => !o.adminOnly || isAdmin),
    [isAdmin],
  );
  const saved = useSelector(selectProductSort);
  // Legacy 'default' maps to the new canonical A-Z option so the row
  // highlights correctly for users whose persisted state predates the
  // migration.
  const normalizedSaved: ProductSortOption =
    saved === 'default' ? 'name_asc' : saved;
  const effectiveSaved = options.some(o => o.key === normalizedSaved)
    ? normalizedSaved
    : 'name_asc';
  const [selected, setSelected] = useState<ProductSortOption>(effectiveSaved);

  useEffect(() => {
    if (effectiveSaved !== saved) dispatch(setProductSort(effectiveSaved));
  }, [effectiveSaved, saved, dispatch]);
  const sheetRef = useRef<BottomSheetModal>(null as any);

  const close = () => setFilterModal(false);
  const apply = () => {
    dispatch(setProductSort(selected));
    close();
  };

  useEffect(() => {
    if (filterModal) {
      sheetRef.current?.present();
    } else {
      sheetRef.current?.dismiss();
    }
  }, [filterModal]);

  const onSheetChange = (index: number) => {
    if (index === -1) setFilterModal(false);
  };

  return (
    <CustomBottomSheet
      bottomSheetModalRef={sheetRef}
      snapPoints={['50%']}
      constrainWidth={true}
      enableDynamicSizing={false}
      handleSheetChanges={onSheetChange}
      header={<Typography style={styles.title}>Sort products by:</Typography>}
      footer={
        <View style={styles.applyFooter}>
          <Button
            text="Apply"
            onPress={apply}
            backgroundColor={theme.colors.success}
            rippleColor={theme.colors.primaryLight}
            height={v(46)}
            paddingHorizontal={s(8)}
            borderRadius={s(8)}
          />
        </View>
      }>
      <View style={styles.sheetContent}>
        <View style={{ flex: 1 }}>
          <View style={{ gap: v(6) }}>
            {options.map(o => (
              <Pressable
                key={o.key}
                onPress={() => setSelected(o.key)}
                style={[styles.row, selected === o.key && styles.rowActive]}>
                <View style={styles.rowLeft}>
                  <MaterialCommunityIcons
                    name={o.icon as any}
                    size={iconSize(20)}
                    color={
                      selected === o.key
                        ? theme.colors.success
                        : theme.colors.text
                    }
                  />
                  <Typography
                    style={[
                      styles.rowLabel,
                      selected === o.key && styles.rowLabelActive,
                    ]}>
                    {o.label}
                  </Typography>
                </View>
                <MaterialCommunityIcons
                  name={
                    selected === o.key
                      ? 'checkbox-marked-circle'
                      : 'checkbox-blank-circle-outline'
                  }
                  size={iconSize(22)}
                  color={
                    selected === o.key
                      ? theme.colors.success
                      : theme.colors.text
                  }
                />
              </Pressable>
            ))}
          </View>
        </View>
      </View>
    </CustomBottomSheet>
  );
};

const createStyles = (theme: any) =>
  StyleSheet.create({
    sheetContent: {
      flex: 1,
      backgroundColor: theme.colors.surface,
      padding: s(10),
      gap: v(12),
    },
    title: {
      fontFamily: GTFonts.bold,
      fontSize: fs(isTablet() ? 18 : 24),
      lineHeight: fs(28),
      color: theme.colors.textDark,
      marginBottom: v(10),
      alignSelf: 'center',
    },
    sectionTitle: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(16),
      color: theme.colors.textDark,
      marginTop: v(6),
    },
    row: {
      paddingVertical: s(16),
      paddingHorizontal: s(8),
      borderRadius: s(8),
      marginBottom: v(8),
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      backgroundColor: theme.colors.background,
    },
    rowActive: {
      borderWidth: borderWidth(Platform.OS === 'web' ? 0.5 : 1),
      borderColor: theme.colors.success,
    },
    rowLeft: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(8),
    },
    rowLabel: {
      color: theme.colors.text,
      fontSize: fs(16),
      lineHeight: fs(22),
    },
    rowLabelActive: {
      color: theme.colors.success,
    },
    applyFooter: {
      paddingTop: v(6),
    },
  });

export default FilterModal;
